import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaUser, FaShieldAlt, FaEnvelope, FaHandHoldingHeart, FaSwimmer, FaTrophy, FaMedal, FaCalendarAlt, FaStar, FaDesktop, FaUsers } from 'react-icons/fa';
import { GiWhistle } from 'react-icons/gi';
import Seo from '../components/Seo';

function Committee() {
  const committeeMembers = [
    {
      role: 'Chairperson',
      name: 'Helen Hannon',
      icon: <FaUser className="text-3xl" />,
      color: 'blue'
    },
    {
      role: 'Welfare Officer',
      name: 'Sue Dunn',
      icon: <FaShieldAlt className="text-3xl" />,
      color: 'green',
      contactLink: '/contact#welfare-officer'
    },
    {
      role: 'Club Secretary',
      name: 'Jacqueline Rees',
      icon: <FaEnvelope className="text-3xl" />,
      color: 'purple'
    },
    {
      role: 'Fundraising Officer',
      name: 'Vacant',
      icon: <FaHandHoldingHeart className="text-3xl" />,
      color: 'orange',
      vacant: true
    },
    {
      role: 'Tadpoles Representative',
      name: 'Jen Walton / Matt Nelson',
      icon: <FaSwimmer className="text-3xl" />,
      color: 'blue'
    },
    {
      role: 'Mavis Wallis Representative',
      name: 'Jen Walton / Matt Nelson',
      icon: <FaSwimmer className="text-3xl" />,
      color: 'green'
    },
    {
      role: 'Intertowns Manager',
      name: 'Lucy Budd & Helen Hannon',
      icon: <FaTrophy className="text-3xl" />,
      color: 'purple'
    },
    {
      role: 'External Meet Coordinator',
      name: 'Lucy Budd / TBC',
      icon: <FaMedal className="text-3xl" />,
      color: 'orange'
    },
    {
      role: 'Swim England Membership Secretary',
      name: 'Alison Bergin',
      icon: <GiWhistle className="text-3xl" />,
      color: 'blue'
    },
    {
      role: 'Membership Secretary',
      name: 'Ellie Cheadle',
      icon: <FaUsers className="text-3xl" />,
      color: 'green'
    },
    {
      role: 'Annual Gala Coordinator',
      name: 'Simon Knighton',
      icon: <FaCalendarAlt className="text-3xl" />,
      color: 'purple'
    },
    {
      role: 'Presentation Evening & Trophy Coordinator',
      name: 'Liz McGill',
      icon: <FaStar className="text-3xl" />,
      color: 'orange'
    },
    {
      role: 'SwimMark Accreditation Coordinator',
      name: 'Catherine Harris',
      icon: <FaMedal className="text-3xl" />,
      color: 'blue'
    },
    {
      role: 'Swim Manager, Website and Kit',
      name: 'Simon Knighton',
      icon: <FaDesktop className="text-3xl" />,
      color: 'green'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: { bg: 'from-blue-50 to-blue-100', border: 'border-blue-500', icon: 'text-blue-600' },
      green: { bg: 'from-green-50 to-green-100', border: 'border-green-500', icon: 'text-green-600' },
      purple: { bg: 'from-purple-50 to-purple-100', border: 'border-purple-500', icon: 'text-purple-600' },
      orange: { bg: 'from-orange-50 to-orange-100', border: 'border-orange-500', icon: 'text-orange-600' }
    };
    return colors[color];
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.05 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.3 }
    }
  };

  return (
    <main className="py-16 bg-gradient-to-b from-gray-50 to-white">
      <Seo
        title="Committee"
        description="Meet the volunteers and committee members who run Marple Swimming Club, including our welfare officer and key contacts."
        path="/committee"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="font-display text-5xl font-bold text-navy-900 mb-6">Our Committee</h1>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            The Committee is largely made up of volunteer parents who dedicate their time to ensure the smooth running of Marple Swimming Club.
          </p>
        </motion.div>

        {/* Meeting Info */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-gradient-to-br from-primary-50 to-primary-100 border-2 border-primary-500 rounded-2xl p-6 mb-16 text-center shadow-lg"
        >
          <h2 className="text-2xl font-bold text-primary-700 mb-3">Monthly Meetings</h2>
          <p className="text-gray-800 text-lg">
            <strong>First Thursday of each month</strong> at 8:00 PM<br />
            The Navigation, Marple
          </p>
          <p className="text-gray-700 mt-3">
            All members welcome! Come along and see how you can get involved.
          </p>
        </motion.div>

        {/* Committee Members Grid */}
        <motion.section
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="mb-16"
        >
          <h2 className="font-display text-4xl font-bold text-navy-900 text-center mb-12">Committee Members</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {committeeMembers.map((member, index) => {
              const colors = getColorClasses(member.color);
              return (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className={`bg-gradient-to-br ${colors.bg} border-2 ${colors.border} rounded-xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 ${member.vacant ? 'opacity-75' : ''}`}
                >
                  <div className={`${colors.icon} mb-4`}>
                    {member.icon}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{member.role}</h3>
                  <p className={`text-gray-800 font-semibold ${member.vacant ? 'italic' : ''}`}>
                    {member.name}
                  </p>
                  {member.contactLink && (
                    <Link
                      to={member.contactLink}
                      className={`inline-block mt-3 text-sm ${colors.icon} font-semibold hover:underline`}
                    >
                      Contact Welfare Officer →
                    </Link>
                  )}
                  {member.vacant && (
                    <p className="text-sm text-orange-700 mt-2 font-medium">
                      We're looking for volunteers!
                    </p>
                  )}
                </motion.div>
              );
            })}
          </div>
        </motion.section>

        {/* Get Involved Section */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <h2 className="font-display text-4xl font-bold text-navy-900 text-center mb-12">Get Involved</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-2xl shadow-lg border-2 border-blue-200 hover:border-blue-500 transition-all duration-300"
            >
              <FaCalendarAlt className="text-4xl text-blue-600 mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Monthly Meetings</h3>
              <p className="text-gray-700 leading-relaxed">
                Committee meetings are held on the first Thursday of each month at 8:00 PM at The Navigation, Marple.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white p-8 rounded-2xl shadow-lg border-2 border-green-200 hover:border-green-500 transition-all duration-300"
            >
              <FaHandHoldingHeart className="text-4xl text-green-600 mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Volunteer Opportunities</h3>
              <p className="text-gray-700 leading-relaxed">
                We're always looking for parents and club members to help with various roles and activities throughout the year.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-white p-8 rounded-2xl shadow-lg border-2 border-purple-200 hover:border-purple-500 transition-all duration-300"
            >
              <FaUsers className="text-4xl text-purple-600 mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">New Members Welcome</h3>
              <p className="text-gray-700 leading-relaxed">
                If you'd like to find out more or assist in any way, speak to a Club official or come along to a meeting – you'll be made very welcome.
              </p>
            </motion.div>
          </div>
        </motion.section>
      </div>
    </main>
  );
}

export default Committee;
